/**
 * Developer API: Artist Shows transform
 *
 * Backs GET /api/artists/{artistId}/shows on developers.punchup.live.
 * Artist shows are native Punchup data; this module owns the DEFAULT-DENY
 * mapping into the shared DeveloperApiShow contract - only named contract
 * fields are emitted, mirroring the venue-shows transform. Fetching, filtering,
 * and pagination live in the sibling `comedian-shows-page.ts`.
 *
 * TIMES: `comedian_shows.datetime` is `timestamp without time zone` holding
 * the venue's WALL time ("9pm" as the poster reads). The contract's `start.local`
 * is that same wall time, so the column passes through untouched - no parse,
 * no zone, no instant. `venues` has no timezone column, so `start.timezone`
 * and `start.utc` are null until a resolver exists.
 */

import { getCDNImageURL } from "@punchup/utils/getCDNImageURL";

import { ShowWithComedian } from "../../legacy/shows";
import { DeveloperApiShow } from "../../models/developer-api.types";
import { punchupShowUrl, toDeveloperApiLineup } from "./shared";

/**
 * The default-deny transform: a Punchup show in, ONLY contract fields out.
 * Never spread the show - every emitted field is named here.
 */
export function showToDeveloperApiShow(
  show: ShowWithComedian
): DeveloperApiShow {
  return {
    id: show.id,
    // Only Tixologi-ticketed shows can be sold through the Checkout embed.
    checkoutEventId: show.tixologi_event_id || null,
    title: show.title || null,
    // Native shows carry no long-form copy - `metadata_text` is a short badge
    // ("18+", "2 drink minimum"), not a description, so emitting it here would
    // mislabel it.
    description: null,
    venue: show.venue || null,
    location: show.location || null,
    start: { local: show.datetime ?? "", timezone: null, utc: null },
    // Native shows record no end.
    end: null,
    isSoldOut: show.is_sold_out ?? false,
    hasLowTickets: show.flags?.includes("LOW_TICKETS") ?? false,
    // Native shows hold no pricing - ticketing lives with the venue's partner,
    // so this endpoint reports "unknown" rather than guessing "not free".
    isFree: null,
    // Categories are a venue-ticketing concept; nothing tags a native show.
    tags: [],
    lineup: toDeveloperApiLineup(show.show_comedians),
    // `poster_img` stores a Cloudflare image id, not a URL - emit the resolved
    // CDN URL, as the lineup transform does for comedian avatars.
    imageUrl: show.poster_img ? getCDNImageURL(show.poster_img) : null,
    ticketUrl: punchupShowUrl(show.id),
  };
}
